import { useEffect, useState } from "react";
import { FiHeart, FiX, FiTrash2 } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import "../../styles/FavoritesButton.css";

const ANIM_MS = 320;

function normalizeLang(lang) {
  return String(lang || "").toLowerCase().startsWith("en") ? "en" : "uk";
}

function getLocalizedName(item, lang = "uk") {
  const safeLang = normalizeLang(lang);

  if (safeLang === "en") {
    return item?.name_en || item?.name || item?.name_uk || "";
  }

  return item?.name_uk || item?.name || item?.name_en || "";
}

function getLocalizedCategory(item, lang = "uk") {
  const safeLang = normalizeLang(lang);

  const categories = Array.isArray(item?.categories) ? item.categories : [];
  const categoryTitles = Array.isArray(item?.category_titles_flat)
    ? item.category_titles_flat
    : [];

  if (categories.length > 0) return categories[0];
  if (categoryTitles.length > 0) return categoryTitles[0];

  if (safeLang === "en") {
    return item?.category_en || item?.category_uk || "";
  }

  return item?.category_uk || item?.category_en || "";
}

function getItemImage(item) {
  if (item?.image_url) return item.image_url;
  if (item?.image_proxy_url) return item.image_proxy_url;

  if (item?.image) return item.image;
  if (item?.img) return item.img;
  if (item?.thumbnail) return item.thumbnail;

  if (Array.isArray(item?.images_json) && item.images_json.length > 0) {
    const first = item.images_json[0];
    if (first?.image_url) return first.image_url;
    if (first?.image_proxy_url) return first.image_proxy_url;
    if (first?.url) return first.url;
  }

  if (Array.isArray(item?.images) && item.images.length > 0) {
    const first = item.images[0];
    if (first?.image_url) return first.image_url;
    if (first?.image_proxy_url) return first.image_proxy_url;
    if (first?.url) return first.url;
  }

  return "";
}

function productKey(item) {
  return String(item?.product_id || item?.id || item?.variant_id || "");
}

export default function FavoritesButton({
  open,
  isActive = false,
  onToggle,
  onClose,
  items = [],
  onOpenProduct,
  onRemoveItem,
  topOffset = 60,
}) {
  const { t, i18n } = useTranslation("favorites");
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);

  const currentLang = normalizeLang(i18n.resolvedLanguage || i18n.language || "uk");

  function formatPrice(price) {
    const n = Number(price || 0);
    const locale = currentLang === "uk" ? "uk-UA" : "en-US";

    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(n);
  }

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      document.body.style.overflow = "hidden";
      return;
    }

    document.body.style.overflow = "";

    if (mounted) {
      setClosing(true);
      const tmr = setTimeout(() => {
        setMounted(false);
        setClosing(false);
      }, ANIM_MS);

      return () => clearTimeout(tmr);
    }
  }, [open, mounted]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }

    if (mounted) {
      document.addEventListener("keydown", onKey);
    }

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [mounted, onClose]);

  const overlayClass = `favOverlay ${open && !closing ? "isOpen" : "isClosing"}`;

  return (
    <div className="favoritesWrap">
      <button
        className={`iconBtn ${isActive ? "isActive" : ""}`}
        aria-label={t("button_aria")}
        title={t("button_title")}
        onClick={(e) => {
          e.stopPropagation();
          onToggle?.();
        }}
      >
        <FiHeart className="iconReact" />
      </button>

      {mounted && (
        <div
          className={overlayClass}
          style={{ paddingTop: topOffset }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
        >
          <div className="favSheet" onClick={(e) => e.stopPropagation()}>
            <div className="favHandleWrap">
              <div className="favHandle" />
            </div>

            <div className="favTopBar">
              <div className="favTitle">
                {t("title")}
                {!!items.length && <span className="favCount">({items.length})</span>}
              </div>

              <button
                type="button"
                className="favCloseBtn"
                onClick={onClose}
                aria-label={t("close")}
              >
                <FiX />
              </button>
            </div>

            <div className="favContent">
              {!items.length ? (
                <div className="favEmptyWrap">
                  <div className="favEmptyTitle">{t("empty_title")}</div>
                  <div className="favEmptyText">{t("empty_text")}</div>
                </div>
              ) : (
                <div className="favList">
                  {items.map((item, idx) => {
                    const img = getItemImage(item);
                    const localizedName =
                      getLocalizedName(item, currentLang) || t("fallback_product");
                    const localizedCategory = getLocalizedCategory(item, currentLang);

                    return (
                      <button
                        key={`${productKey(item) || "fav"}-${idx}`}
                        type="button"
                        className="favItem"
                        onClick={() => {
                          onOpenProduct?.(item);
                          onClose?.();
                        }}
                      >
                        <div className="favItemThumbWrap">
                          {img ? (
                            <img
                              className="favItemImage"
                              src={img}
                              alt={localizedName}
                            />
                          ) : (
                            <div className="favItemImage favItemImagePlaceholder" />
                          )}
                        </div>

                        <div className="favItemBody">
                          <div className="favItemTopRow">
                            <div className="favItemName">{localizedName}</div>

                            <button
                              type="button"
                              className="favRemoveBtn"
                              aria-label={t("remove")}
                              title={t("remove")}
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemoveItem?.(item);
                              }}
                            >
                              <FiTrash2 />
                            </button>
                          </div>

                          {!!localizedCategory && (
                            <div className="favItemMeta">{localizedCategory}</div>
                          )}

                          <div className="favItemBottom">
                            <div className="favItemPrice">
                              {t("currency_symbol")}{formatPrice(item?.price)}
                            </div>

                            {!!item?.old_price &&
                              Number(item.old_price) > Number(item.price) && (
                                <div className="favItemOldPrice">
                                  {t("currency_symbol")}{formatPrice(item?.old_price)}
                                </div>
                              )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}